import type { AttachmentDto, PageAttachmentDto } from '@/api/attachment';
import { http } from '@/api/http';
import { appendJsonPart } from '@/api/multipart';

export const documentsApi = {
  /**
   * GET /persons/{guid}/attachment — patient attachment list (the BE name
   * for what the UI calls "documents"). Returns a Spring `Page<Attachment>`;
   * the mapper unwraps `.content`.
   *
   * Page size defaults to 100, matching the BE default — the UI lists every
   * row today and does not paginate. Revisit if a single record routinely
   * exceeds this.
   */
  getDocuments: (
    guid: string,
    options?: { signal?: AbortSignal; pageNumber?: number; pageSize?: number },
  ): Promise<PageAttachmentDto> => {
    const { pageNumber = 0, pageSize = 100, ...rest } = options ?? {};
    return http.get<PageAttachmentDto>(
      `/persons/${encodeURIComponent(guid)}/attachment`,
      { ...rest, query: { pageNumber, pageSize } },
    );
  },

  /**
   * POST /persons/{guid}/attachment — multipart upload. Sends the binary as
   * `file`, the FE-picked category as `type`, and the user-typed title as
   * `comments` (BE has no dedicated title field). `fileName` is derived
   * server-side from the uploaded file.
   *
   * Non-file parts go over as `application/json` blobs so Spring's
   * `@RequestPart` resolver accepts them — plain-string parts land as
   * `application/octet-stream` and return 415. See `appendJsonPart`.
   */
  uploadDocument: (
    guid: string,
    body: {
      title: string;
      document_type: string;
      file?: File;
    },
    options?: { signal?: AbortSignal },
  ): Promise<AttachmentDto> => {
    const formData = new FormData();
    appendJsonPart(formData, 'type', body.document_type);
    appendJsonPart(formData, 'comments', body.title);
    if (body.file !== undefined) formData.append('file', body.file);
    return http.post<AttachmentDto>(
      `/persons/${encodeURIComponent(guid)}/attachment`,
      formData,
      options,
    );
  },

  /** DELETE /persons/{guid}/attachment/{id}. */
  deleteDocument: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/persons/${encodeURIComponent(guid)}/attachment/${encodeURIComponent(id)}`,
      options,
    ),
};
